/**
 * Kit Upsell Popup (Padel USA custom)
 * ---------------------------------------------------------------------------
 * Opens a "Complete your kit" dialog after a product is added to the cart, lets
 * the shopper add complementary essentials, then routes to cart/checkout.
 * Flow: add to cart -> popup -> /cart (or /checkout in the recap variant).
 *
 * Opens for ALL products by default (data-open-for-all="true" in the snippet) so
 * every add gets the same confirmation flow — the essentials are universal padel
 * gear. Already-in-cart curated add-ons are hidden so the popup never re-offers an
 * item the shopper just added. Set data-open-for-all="false" to fall back to the
 * racket-only gate (data-racket-types matchers on product_type/tags).
 *
 * A/B VARIANTS (Intelligems-driven):
 *  - recap (DEFAULT, current live winner): added-item recap + subtotal +
 *    View cart / Checkout CTAs ABOVE the add-on list.
 *  - recap-below (challenger): IDENTICAL recap layout, but the View cart / Checkout
 *    CTAs render BELOW the add-on list (CSS `order` on the panel's flex children;
 *    carries both `kit-upsell--recap` and `kit-upsell--recap-below`). Isolates the
 *    CTA-position variable for a clean A/B (recap vs recap-below).
 *  - essentials (legacy): headline-first, single "go to cart" CTA. Not in the
 *    current test, kept for reference.
 * Variant resolved at open time from (in order): window.PADEL_UPSELL_VARIANT ->
 * <html data-upsell-variant> -> cookie `upsell_variant` -> data-default-variant
 * (now `recap`). NOTE: with recap as the baseline, an Intelligems treatment that
 * sets window.PADEL_UPSELL_VARIANT='recap' is a no-op (everyone gets recap). To
 * run the CTA-position test, the challenger arm forces 'recap-below'.
 *
 * Event-driven: listens for the theme `cart:update` (CartAddEvent), only reacts
 * to genuine product-form adds. Reuses DialogComponent.
 *
 * Analytics (2026-10-09, upsell ranking step 1): same contract as assets/build-your-kit.js —
 * `kit_upsell_shown` (once per filled open: the visible rows as {slot, product_id, variant_id,
 * price_cents, position}) and `kit_upsell_added` (per committed add-on, same item shape) go to
 * dataLayer, a document CustomEvent and Shopify.analytics.publish. The legacy
 * `kit_upsell_addon_added` (variant + variant_id) still fires for live Intelligems goals. Rows render
 * in the order of the shop metafield custom.popup_upsell_priority (snippets/kit-upsell-popup.liquid).
 *
 * Docs: custom-code-padelusa/features/kit-upsell-popup.md
 */
import { CartAddEvent } from '@theme/events';
import { DialogComponent } from '@theme/dialog';
import { ensureShippingProtectionTier } from '@theme/shipping-protection-tier';

const ROUTES =
  typeof Theme !== 'undefined' && Theme.routes
    ? Theme.routes
    : { cart_url: '/cart', cart_add_url: '/cart/add.js', cart_change_url: '/cart/change.js' };

const VARIANTS = ['essentials', 'recap', 'recap-below'];

/** Line-item `_added_from` value and the analytics `placement` label. */
const SOURCE = 'kit-upsell-popup';

/** recap + recap-below share the same content/layout; only CTA position differs. */
const isRecapFamily = (variant) => variant === 'recap' || variant === 'recap-below';

class KitUpsellDialog extends DialogComponent {
  /** @type {Set<string>} */
  #selected = new Set();

  /**
   * Variant ids with an in-flight cart write (add/remove), so a double-tap can't
   * fire two requests for the same row. Mirrors the optimistic UI state.
   * @type {Set<string>}
   */
  #busy = new Set();

  /**
   * In-flight cart-write promises. #goWithSelected awaits these before navigating
   * so a fast "Add -> View cart" never leaves the page before /cart/add.js lands.
   * @type {Array<Promise<unknown>>}
   */
  #inflight = [];

  /**
   * Optimistic-open state: set when the dialog opened at submit time, before the
   * /cart/add.js + /cart.js round-trips (~1.7s on production) have confirmed the add.
   * Cleared by #settlePending once the CartAddEvent fills in the real cart numbers.
   * `dismissed` flips true when the shopper closes the optimistic dialog before that fill
   * (see closeDialog below), so the late CartAddEvent settles quietly instead of reopening it.
   * @type {{ timer: number, waiters: Array<() => void>, dismissed: boolean } | null}
   */
  #pendingAdd = null;

  /**
   * Last-known cart snapshot ({ item_count, total_price }) used to render an OPTIMISTIC
   * subtotal the instant the popup opens — otherwise the subtotal only appears ~1.7s later,
   * once the add POST + CartAddEvent resolve (the "late pop-in"). Primed on connect and
   * refreshed from every authoritative cart (CartAddEvent fill, add-on commits). Null until
   * the first fetch → falls back to hide-until-loaded.
   * @type {{ item_count: number, total_price: number } | null}
   */
  #cachedCart = null;

  // No add-on writes until an optimistic open has authoritative cart lines.
  #addonsPending = false;

  #onCartAdd = (event) => this.#handleCartAdd(event);

  // Every close (X, Continue shopping, Escape, outside click) goes through closeDialog, so
  // mark a still-pending optimistic open as dismissed synchronously, before the async close.
  // (The dialog's own `close` event fires too late: it can land on the NEXT add's pending.)
  #baseCloseDialog = this.closeDialog;
  closeDialog = (...args) => {
    if (this.#pendingAdd) this.#pendingAdd.dismissed = true;
    return this.#baseCloseDialog.apply(this, args);
  };

  connectedCallback() {
    super.connectedCallback();
    // Signal readiness FIRST so the inline early-buffer (kit-upsell-popup.liquid) stops
    // capturing the moment our real listener is about to attach (no await in between → no race).
    if (typeof window !== 'undefined') window.__kitUpsellReady = true;
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
    // Capture-phase: open optimistically the instant the shopper submits a product form,
    // instead of waiting ~1.7s for the add POST + cart.js fetch to resolve.
    document.addEventListener('submit', this.#onFormSubmit, true);
    this.#buildVariantChoices();
    this.#bindControls();
    document.addEventListener('pointerdown', this.#onChoiceOutside, true);
    document.addEventListener('keydown', this.#onChoiceEscape, true);
    this.refs.dialog?.addEventListener('close', this.#onDialogClose);
    this.#drainPendingAdd();
    // Prime the cart snapshot in the background so the first add can show a subtotal instantly.
    this.#primeCartCache();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.removeEventListener('submit', this.#onFormSubmit, true);
    document.removeEventListener('pointerdown', this.#onChoiceOutside, true);
    document.removeEventListener('keydown', this.#onChoiceEscape, true);
    this.refs.dialog?.removeEventListener('close', this.#onDialogClose);
    this.#closeChoicePopover();
  }

  /**
   * Open the popup at submit time (optimistic). Item identity comes from the
   * data-kit-item-* attributes on <product-form-component> (blocks/buy-buttons-custom.liquid);
   * the real cart totals stream in via CartAddEvent and #handleCartAdd fills them.
   * Quick-add submits happen inside a <dialog> and keep the event-driven path (close-race).
   */
  #onFormSubmit = (event) => {
    try {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const component = form.closest('product-form-component');
      if (!component) return;
      if (this.refs.dialog?.open || this.#pendingAdd) return;
      if (document.documentElement.classList.contains('shopify-design-mode')) return;
      if (!this.querySelector('[data-kit-add]')) return;

      const type = String(component.dataset.kitItemType || '').toLowerCase();
      const tags = String(component.dataset.kitItemTags || '')
        .toLowerCase()
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
      const isAnchor = this.#matchers.some((matcher) => type.includes(matcher) || tags.includes(matcher));
      if (!this.#openForAll && !isAnchor) return;

      // Quick-add submits happen inside the quick-add modal <dialog>. Close it right away
      // (the add request keeps running) and open ours behind it — #openPending awaits the
      // close, preserving the v4 "never two open dialogs" invariant at ~200ms (modal close
      // animation) instead of the ~1.7s network round-trip.
      const hostDialog = form.closest('dialog');
      if (hostDialog) this.#closeHostDialog(hostDialog);

      this.#openPending(component);
    } catch (error) {
      /* never interfere with the actual add-to-cart submit */
    }
  };

  /** @param {HTMLElement} component */
  async #openPending(component) {
    const itemLite = {
      product_title: component.dataset.kitItemTitle || '',
      product_type: component.dataset.kitItemType || '',
      image: component.dataset.kitItemImage || '',
    };
    this.#pendingAdd = {
      waiters: [],
      dismissed: false,
      // If no CartAddEvent ever lands (network failure → product-form only console.errors),
      // close rather than pretend the item was added.
      timer: window.setTimeout(() => this.#settlePending({ close: true }), 7000),
    };

    // Quick-add path: the host modal was just told to close — wait for it to finish so the
    // two dialogs' scroll-lock handoffs never collide. Resolves immediately when none are open.
    await this.#waitForOtherDialogsToClose();
    // Settled while waiting (didError closed us) or the evented path already opened the dialog
    // with real cart data — don't double-open.
    if (!this.#pendingAdd || this.refs.dialog?.open) return;

    const variant = this.#resolveVariant();
    const recap = isRecapFamily(variant);
    this.classList.toggle('kit-upsell--recap', recap);
    this.classList.toggle('kit-upsell--recap-below', variant === 'recap-below');
    this.classList.toggle('kit-upsell--essentials', !recap);
    if (recap) {
      // Show the subtotal immediately when we can estimate it (cached cart + this item's
      // price × qty); otherwise hide it (CSS: .kit-upsell--pending) until the CartAddEvent
      // brings the authoritative numbers. Either way #populateRecap reconciles on fill.
      const optimistic = this.#optimisticCart(component);
      if (optimistic) {
        this.#populateRecap(optimistic, itemLite);
      } else {
        this.classList.add('kit-upsell--pending');
        this.#populateRecap({ item_count: null, total_price: null }, itemLite);
      }
    } else {
      this.#setEyebrow(itemLite);
    }
    this.#reset();
    // Keep add-ons unavailable until the CartAddEvent reconciles real cart lines.
    this.#syncAddons(null);
    this.showDialog();
    this.dispatchEvent(new CustomEvent('kit-upsell:shown', { bubbles: true, detail: { variant, pending: true } }));
  }

  /** Clear the pending state, release CTA clicks waiting on it, optionally close the dialog. */
  #settlePending({ close = false } = {}) {
    this.classList.remove('kit-upsell--pending');
    const pending = this.#pendingAdd;
    if (!pending) return;
    this.#pendingAdd = null;
    clearTimeout(pending.timer);
    pending.waiters.forEach((resolve) => resolve());
    if (close && this.refs.dialog?.open) this.closeDialog();
  }

  /** Resolves once any optimistic add has been confirmed (immediately when none is pending). */
  #whenPendingSettled() {
    if (!this.#pendingAdd) return Promise.resolve();
    return new Promise((resolve) => this.#pendingAdd?.waiters.push(resolve));
  }

  /**
   * Close the dialog component hosting `dialogEl` (e.g. the quick-add modal) via its own
   * closeDialog() so its animations/cleanup run; fall back to the native close.
   * @param {HTMLElement} dialogEl
   */
  #closeHostDialog(dialogEl) {
    let node = dialogEl;
    while (node && typeof (/** @type {any} */ (node).closeDialog) !== 'function') {
      node = node.parentElement;
    }
    if (node) /** @type {any} */ (node).closeDialog();
    else if (typeof (/** @type {any} */ (dialogEl)).close === 'function') /** @type {any} */ (dialogEl).close();
  }

  /**
   * Replay a product-form add that fired before this module finished loading. A tiny inline
   * listener in kit-upsell-popup.liquid buffers the latest such add into window.__kitUpsellPending,
   * so a fast add right after page load is never missed (the popup-loads-too-late symptom).
   */
  #drainPendingAdd() {
    const pending = typeof window !== 'undefined' ? window.__kitUpsellPending : null;
    if (!pending) return;
    window.__kitUpsellPending = null;
    // Ignore stale buffers (e.g. left over from an earlier navigation in the same tab).
    if (!pending.at || Date.now() - pending.at > 10000) return;
    this.#handleCartAdd({ detail: { resource: pending.resource, data: pending.data } });
  }

  /** Lowercased product-type / tag substrings that count as an anchor (racket). */
  get #matchers() {
    return (this.dataset.racketTypes || 'racket')
      .toLowerCase()
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  /**
   * When true, the popup opens for EVERY product add (not just rackets) — the
   * essentials are universal padel gear, so a consistent "added to cart" flow
   * across the board is less confusing than silence on non-racket adds. The
   * #matchers/#isAnchor logic stays as the fallback path when this is off.
   */
  get #openForAll() {
    return this.dataset.openForAll === 'true';
  }

  // A picker chooses a variant; it never adds one. Previously committed colors/sizes
  // remain in #selected and in the cart, so returning to one offers its Remove toggle.
  #syncVariantSelect(select) {
    const row = select.closest('[data-kit-item]');
    const button = row?.querySelector('[data-kit-add]');
    if (!button) return;
    const option = select.selectedOptions[0];
    const id = option && !option.disabled ? option.value : '';
    button.setAttribute('data-variant-id', id);
    button.disabled = this.#addonsPending;
    const addLabel = button.querySelector('[data-kit-add-label]');
    if (addLabel) addLabel.textContent = id ? this.#label('labelAdd', 'Add') : this.#label('labelChoose', 'Choose');
    button.setAttribute('aria-pressed', this.#selected.has(id) ? 'true' : 'false');
    row.classList.toggle('is-selected', this.#selected.has(id));
    const available = Array.from(select.options).filter((candidate) => candidate.value && !candidate.disabled);
    const cleanPrice = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();
    const price = row.querySelector('[data-kit-variant-price]');
    if (price) {
      const firstPrice = cleanPrice((available.find((candidate) => candidate.dataset.color === row.dataset.kitColor) || available[0])?.dataset.price);
      price.textContent = id ? cleanPrice(option.dataset.price) : firstPrice ? this.#label('labelFromPrice', 'From {price}').replace('{price}', firstPrice) : '';
    }
    const image = row.querySelector('[data-kit-variant-image]');
    if (image && id) {
      if (option.dataset.image) image.setAttribute('src', option.dataset.image);
      else image.removeAttribute('src');
      image.alt = `Floky NO STRAIN Evolution sleeve — ${option.textContent.trim()}`;
    }
    this.#refreshVariantChoices(select, available);
  }

  // Reuse Horizon's variant-option button labels for a compact, keyboard-accessible
  // color-then-size choice. The native select stays as a no-JS fallback and data source.
  #buildVariantChoices() {
    this.querySelectorAll('[data-kit-variant-select]').forEach((select) => {
      const row = select.closest('[data-kit-group]');
      const panel = row?.querySelector('[data-kit-variant-choices]');
      if (!panel || panel.dataset.kitBuilt === 'true') return;
      const options = Array.from(select.options).filter((option) => option.value);
      if (!options.length || options.some((option) => !option.dataset.color || !option.dataset.size)) return;
      for (const [kind, values] of [
        ['color', [...new Set(options.map((option) => option.dataset.color))]],
        ['size', [...new Set(options.map((option) => option.dataset.size))]],
      ]) {
        const container = panel.querySelector(`[data-kit-${kind}-options]`);
        for (const value of values) {
          const label = document.createElement('label');
          label.className = 'variant-option__button-label kit-upsell__choice';
          const input = document.createElement('input');
          input.className = 'visually-hidden';
          input.type = 'radio';
          input.name = `kit-floky-${kind}`;
          input.value = value;
          input.dataset.kitChoice = kind;
          const text = document.createElement('span');
          text.textContent = value;
          label.append(input);
          if (kind === 'color' && /^(black|white)$/i.test(value)) {
            const swatch = document.createElement('span');
            swatch.className = `kit-upsell__swatch kit-upsell__swatch--${value.toLowerCase()}`;
            swatch.setAttribute('aria-hidden', 'true');
            label.append(swatch);
          }
          label.append(text);
          container.append(label);
        }
      }
      panel.dataset.kitBuilt = 'true';
      select.hidden = true;
      row.querySelector('.kit-upsell__variant-fallback-label').hidden = true;
      this.#syncVariantSelect(select);
    });
  }

  #refreshVariantChoices(select, available = Array.from(select.options).filter((option) => option.value && !option.disabled)) {
    const row = select.closest('[data-kit-group]');
    const panel = row?.querySelector('[data-kit-variant-choices]');
    if (!panel || panel.dataset.kitBuilt !== 'true') return;
    const selected = select.selectedOptions[0];
    if (selected?.value) {
      row.dataset.kitColor = selected.dataset.color;
      row.dataset.kitSize = selected.dataset.size;
    }
    let color = row.dataset.kitColor || '';
    let size = row.dataset.kitSize || '';
    if (!available.some((option) => option.dataset.color === color)) color = size = '';
    if (!available.some((option) => option.dataset.color === color && option.dataset.size === size)) size = '';
    row.dataset.kitColor = color;
    row.dataset.kitSize = size;
    const edit = row.querySelector('[data-kit-choice-trigger]');
    if (edit) {
      const editText = selected?.value ? `${color} / ${size} · ${this.#label('labelChange', 'Change')}` : this.#label('labelColorSizeShort', 'Color & size');
      const summary = edit.querySelector('[data-kit-choice-summary]');
      if (summary && summary.textContent !== editText) summary.textContent = editText;
      edit.setAttribute('aria-label', selected?.value ? this.#label('labelChangeFlokyAria', 'Change Floky color and size, current {current}').replace('{current}', `${color} / ${size}`) : this.#label('labelChooseFlokyAria', 'Choose Floky color and size'));
    }
    panel.querySelectorAll('[data-kit-choice]').forEach((input) => {
      const isColor = input.dataset.kitChoice === 'color';
      input.checked = input.value === (isColor ? color : size);
      input.disabled = select.disabled || !available.some((option) =>
        isColor ? option.dataset.color === input.value
          : option.dataset.color === color && option.dataset.size === input.value
      );
    });
    const hint = panel.querySelector('[data-kit-size-hint]');
    if (hint) hint.textContent = color ? this.#label('labelChooseSize', 'Choose your size.') : this.#label('labelChooseColorFirst', 'Choose a color first.');
    const image = row.querySelector('[data-kit-variant-image]');
    const colorOption = available.find((candidate) => candidate.dataset.color === color);
    if (!selected?.value && image && colorOption?.dataset.image) {
      image.src = colorOption.dataset.image;
      image.alt = `Floky NO STRAIN Evolution sleeve — ${color}`;
    }
  }

  #openChoicePopover() {
    const panel = this.querySelector('[data-kit-variant-choices]');
    const trigger = this.querySelector('[data-kit-choice-trigger]');
    if (!panel || !trigger || panel.dataset.open === 'true') return;
    panel.hidden = false;
    try { panel.showPopover?.(); } catch { /* fixed-position fallback */ }
    panel.dataset.open = 'true';
    trigger.setAttribute('aria-expanded', 'true');
    panel.querySelector('[data-kit-color-options] input:not(:disabled)')?.focus();
  }

  #closeChoicePopover(restoreFocus = false) {
    const panel = this.querySelector('[data-kit-variant-choices]');
    const trigger = this.querySelector('[data-kit-choice-trigger]');
    if (!panel || panel.dataset.open !== 'true') return;
    if (panel.hidePopover && panel.matches(':popover-open')) panel.hidePopover();
    panel.hidden = true;
    panel.dataset.open = 'false';
    trigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) trigger?.focus();
  }

  #onChoiceOutside = (event) => {
    const panel = this.querySelector('[data-kit-variant-choices]');
    const target = event.target;
    if (panel?.dataset.open !== 'true' || !(target instanceof Element)) return;
    if (panel.contains(target) || target.closest('[data-kit-choice-trigger]')) return;
    this.#closeChoicePopover();
  };

  #onChoiceEscape = (event) => {
    if (event.key !== 'Escape' || this.querySelector('[data-kit-variant-choices]')?.dataset.open !== 'true') return;
    event.preventDefault();
    event.stopPropagation();
    this.#closeChoicePopover(true);
  };

  #onDialogClose = () => this.#closeChoicePopover();

  #bindControls() {
    this.addEventListener('change', (event) => {
      const control = event.target;
      if (control instanceof HTMLInputElement && control.matches('[data-kit-choice]')) {
        const row = control.closest('[data-kit-group]');
        const select = row?.querySelector('[data-kit-variant-select]');
        if (!select || select.disabled) return;
        if (control.dataset.kitChoice === 'color') {
          row.dataset.kitColor = control.value;
          row.dataset.kitSize = '';
          select.value = '';
        } else {
          row.dataset.kitSize = control.value;
          select.value = Array.from(select.options).find((option) =>
            option.value && !option.disabled &&
            option.dataset.color === row.dataset.kitColor &&
            option.dataset.size === control.value
          )?.value || '';
        }
        select.dispatchEvent(new Event('change', { bubbles: true }));
        if (control.dataset.kitChoice === 'size' && select.value) {
          this.#closeChoicePopover();
          row.querySelector('[data-kit-add]')?.focus();
        }
        return;
      }
      const select = control;
      if (!(select instanceof HTMLSelectElement) || !select.matches('[data-kit-variant-select]')) return;
      // Native disabled controls cannot change during a write; ignore synthetic changes too.
      if (select.disabled) {
        select.value = select.closest('[data-kit-item]').querySelector('[data-kit-add]').dataset.variantId;
        this.#refreshVariantChoices(select);
        return;
      }
      this.#syncVariantSelect(select);
    });
    // Whole-card toggle (mirrors the cart add-ons, assets/cart-addons.js): a click ANYWHERE on an
    // add-on card selects/deselects it — not just the "Add" button. The button lives inside
    // [data-kit-item], so its own clicks resolve through this same delegated handler, meaning there
    // is NO separate button listener and therefore no double-toggle. Delegated on the host element.
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-kit-choice-trigger]')) {
        if (this.querySelector('[data-kit-variant-choices]')?.dataset.open === 'true') this.#closeChoicePopover(true);
        else this.#openChoicePopover();
        return;
      }
      if (event.target.closest('[data-kit-choice-close]')) {
        this.#closeChoicePopover(true);
        return;
      }
      const item = event.target.closest('[data-kit-item]');
      if (!item) return;
      if (item.hasAttribute('data-kit-group') && !event.target.closest('[data-kit-add]')) return;
      const button = item.querySelector('[data-kit-add]');
      if (item.hasAttribute('data-kit-group') && button && !button.dataset.variantId) {
        this.#openChoicePopover();
        return;
      }
      if (button) this.#toggle(button);
    });
    this.querySelectorAll('[data-kit-close]').forEach((button) =>
      button.addEventListener('click', () => this.closeDialog())
    );
    this.querySelector('[data-kit-cta]')?.addEventListener('click', () => this.#goWithSelected(ROUTES.cart_url));
    this.querySelector('[data-kit-viewcart]')?.addEventListener('click', () => this.#goWithSelected(ROUTES.cart_url));
    this.querySelector('[data-kit-checkout]')?.addEventListener('click', () => this.#goWithSelected('/checkout'));
  }

  /** Resolve the A/B variant (Intelligems sets one of the first three signals). */
  #resolveVariant() {
    const candidates = [
      typeof window !== 'undefined' ? window.PADEL_UPSELL_VARIANT : null,
      document.documentElement.dataset.upsellVariant,
      this.#cookie('upsell_variant'),
      this.dataset.defaultVariant,
    ];
    for (const candidate of candidates) {
      const value = String(candidate || '').toLowerCase();
      if (VARIANTS.includes(value)) return value;
    }
    return 'recap';
  }

  #cookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  async #handleCartAdd(event) {
    const data = event?.detail?.data ?? {};
    if (!String(data.source || '').includes('product-form')) return;
    if (data.didError) {
      // An optimistically-opened dialog must not survive a failed add.
      this.#settlePending({ close: true });
      return;
    }
    // The shopper closed the optimistic popup before the add confirmed: that's a dismissal.
    // Without this, the closed dialog fell through to the evented open below and reopened.
    if (this.#pendingAdd?.dismissed) {
      this.#settlePending();
      // Keep the next open's optimistic subtotal honest (this add did land).
      this.#primeCartCache();
      return;
    }
    // When we opened optimistically at submit time, this event is the FILL step:
    // swap the placeholder totals for the real cart numbers.
    const pendingFill = Boolean(this.#pendingAdd && this.refs.dialog?.open);
    if (this.refs.dialog?.open && !pendingFill) return;
    if (document.documentElement.classList.contains('shopify-design-mode')) return;
    if (!this.querySelector('[data-kit-add]')) return;

    // Prefer the cart already attached to the add-to-cart event; only fetch as a fallback.
    // Skipping the /cart.js round-trip lets the popup open almost immediately.
    const resource = event?.detail?.resource;
    // A close/reopen can race an earlier popup write. Its event cart predates that
    // write, so settle it and fetch authoritative lines before resetting selection.
    const hadInflight = this.#inflight.length > 0;
    if (hadInflight) await Promise.allSettled(this.#inflight);
    const cart = !hadInflight && resource && Array.isArray(resource.items) ? resource : await this.#getCart();
    if (!cart) {
      this.#settlePending();
      return;
    }
    const item = this.#findAddedItem(cart, data.variantId);
    if (!item || (!this.#openForAll && !this.#isAnchor(item))) {
      // Shouldn't happen on the optimistic path (submit was gated) — close rather than lie.
      this.#settlePending({ close: true });
      return;
    }
    const recapItem = data.quantityUpsellSizes
      ? { ...item, options_with_values: [{ name: 'Sizes', value: data.quantityUpsellSizes }] }
      : item;

    if (pendingFill) {
      // No choices were available during the optimistic open. Clear prior-session
      // selections only after any earlier writes have settled.
      this.#reset();
      this.#applyVariant(this.#resolveVariant(), cart, recapItem);
      this.#settlePending();
      return;
    }

    // Quick-add adds from inside a modal dialog. Wait for that dialog to fully close
    // (its scroll-lock / body styles are reset on close) before opening ours — otherwise
    // the two modals race and our popup can fail to appear.
    await this.#waitForOtherDialogsToClose();
    if (this.refs.dialog?.open) return;

    this.#reset();
    this.#applyVariant(this.#resolveVariant(), cart, recapItem);
    this.showDialog();
    // If an optimistic open is still mid-wait (it lost the race to this evented open),
    // settle it so its 7s safety timer can't close the dialog we just opened.
    this.#settlePending();
  }

  /**
   * Resolve once any other open <dialog> (e.g. the quick-add modal) has closed, so its
   * scroll-lock handoff doesn't collide with ours. Resolves immediately when none are open.
   * @returns {Promise<void>}
   */
  #waitForOtherDialogsToClose() {
    const others = Array.from(document.querySelectorAll('dialog[open]')).filter(
      (dialog) => dialog !== this.refs.dialog
    );
    if (others.length === 0) return Promise.resolve();

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearInterval(timer);
        clearTimeout(safety);
        others.forEach((dialog) => dialog.removeEventListener('close', check));
        resolve();
      };
      const check = () => {
        if (others.every((dialog) => !dialog.open)) finish();
      };
      others.forEach((dialog) => dialog.addEventListener('close', check));
      const timer = setInterval(check, 50);
      const safety = setTimeout(finish, 1500);
    });
  }

  async #getCart() {
    try {
      const response = await fetch(`${ROUTES.cart_url}.js`, { headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  /** Refresh #cachedCart from /cart.js (fire-and-forget; used to seed the optimistic subtotal). */
  async #primeCartCache() {
    const cart = await this.#getCart();
    if (cart && cart.total_price != null) {
      this.#cachedCart = { item_count: cart.item_count || 0, total_price: cart.total_price || 0 };
    }
  }

  /**
   * Best-effort subtotal to render the instant the popup opens: cached cart total + this
   * item's price × qty. Returns null when we lack a cached cart or the item's price (then
   * the caller falls back to hiding the subtotal until the CartAddEvent lands). The real
   * numbers reconcile on fill, so a rare mismatch (e.g. a cart-level discount) self-corrects.
   */
  #optimisticCart(component) {
    const cache = this.#cachedCart;
    if (!cache) return null;
    const price = parseInt(component?.dataset.kitItemPrice || '', 10);
    if (!Number.isFinite(price)) return null;
    const qty = Math.max(1, parseInt(component.querySelector('[name="quantity"]')?.value || '1', 10) || 1);
    return {
      item_count: (cache.item_count || 0) + qty,
      total_price: (cache.total_price || 0) + price * qty,
    };
  }

  #findAddedItem(cart, variantId) {
    const items = cart.items || [];
    const item = variantId ? items.find((line) => String(line.variant_id) === String(variantId)) : null;
    // Shopify's cart.items is NEWEST-FIRST — when the variant lookup misses, the just-added
    // line is items[0]. (The old items[items.length - 1] fallback showed the OLDEST cart line:
    // live-verified wrong item in the recap after a card quick-add.)
    // Never recap the Shipping Protection line: when the shopper re-adds a product already in
    // the cart, the newest line can be protection (added in this popup or on /cart).
    const merchandise = items.find((line) => !line.properties?._shipping_protection && line.handle !== 'shipping-protection');
    return item || merchandise || items[0] || null;
  }

  #isAnchor(item) {
    const type = String(item.product_type || '').toLowerCase();
    const tags = (item.tags || []).map((tag) => String(tag).toLowerCase());
    return this.#matchers.some((matcher) => type.includes(matcher) || tags.includes(matcher));
  }

  #applyVariant(variant, cart, item) {
    const recap = isRecapFamily(variant);
    this.classList.toggle('kit-upsell--recap', recap);
    this.classList.toggle('kit-upsell--recap-below', variant === 'recap-below');
    this.classList.toggle('kit-upsell--essentials', !recap);
    if (recap) this.#populateRecap(cart, item);
    else this.#setEyebrow(item);
    this.#syncAddons(cart);
    // Authoritative cart → refresh the snapshot so the NEXT open's optimistic subtotal is accurate.
    if (cart && cart.total_price != null) {
      this.#cachedCart = { item_count: cart.item_count || 0, total_price: cart.total_price || 0 };
    }
    this.dispatchEvent(new CustomEvent('kit-upsell:shown', { bubbles: true, detail: { variant } }));
    this.#track('kit_upsell_shown', {
      variant,
      trigger_product_id: String(item?.product_id || ''),
      trigger_product_type: String(item?.product_type || ''),
      items: this.#visibleRows().map((row, index) => this.#rowInfo(row, index + 1)),
    });
  }

  /** Add-on rows currently offered (#syncAddons hides in-cart rows with display:none). */
  #visibleRows() {
    return [...this.querySelectorAll('[data-kit-item]')].filter((row) => row.style.display !== 'none');
  }

  /** Analytics fields for one row; a choice row (Floky) reports its chosen variant once picked. */
  #rowInfo(row, position) {
    const button = row.querySelector('[data-kit-add]');
    const option = row.querySelector('[data-kit-variant-select]')?.selectedOptions[0];
    const chosen = option && option.value ? option : null;
    return {
      slot: row.dataset.kitSlot || '',
      product_id: chosen?.dataset.productId || row.dataset.kitProductId || '',
      variant_id: button?.dataset.variantId || '',
      price_cents: Number(chosen ? chosen.dataset.priceCents : button?.dataset.priceCents) || 0,
      position,
    };
  }

  /**
   * Hide curated add-ons that are already in the cart (e.g. the just-added item when it IS
   * an essential) so the popup never offers something the shopper already has. Reconciles
   * full visibility each call: null/no-cart displays rows but locks their controls until
   * authoritative cart lines arrive. When nothing is left to add, the essentials header + list hide too so
   * there's no dangling "ADD THE ESSENTIALS" section over an empty list.
   * @param {{items?: Array<{variant_id: number|string}>}|null} cart
   */
  #syncAddons(cart) {
    this.#addonsPending = !cart;
    const inCart = new Set((cart?.items || []).map((line) => String(line.variant_id)));
    let visible = 0;
    this.querySelectorAll('[data-kit-item]').forEach((row) => {
      const id = row.querySelector('[data-kit-add]')?.getAttribute('data-variant-id');
      const select = row.querySelector('[data-kit-variant-select]');
      row.querySelector('[data-kit-add]').disabled = this.#addonsPending;
      let hide = id ? inCart.has(String(id)) : false;
      if (select) {
        select.disabled = this.#addonsPending;
        for (const option of select.options) {
          if (option.value) {
            option.disabled = inCart.has(option.value) && !this.#selected.has(option.value);
            option.hidden = option.disabled;
          }
        }
        if (select.selectedOptions[0]?.disabled) select.value = '';
        this.#syncVariantSelect(select);
        hide = !Array.from(select.options).some((option) => option.value && !option.disabled);
      }
      /** @type {HTMLElement} */ (row).style.display = hide ? 'none' : '';
      if (!hide) visible += 1;
    });
    const title = /** @type {HTMLElement|null} */ (this.querySelector('.kit-upsell__list-title'));
    const list = /** @type {HTMLElement|null} */ (this.querySelector('.kit-upsell__list'));
    if (title) title.style.display = visible === 0 ? 'none' : '';
    if (list) list.style.display = visible === 0 ? 'none' : '';
  }

  #setEyebrow(item) {
    const eyebrow = this.querySelector('[data-kit-eyebrow]');
    if (!eyebrow) return;
    eyebrow.textContent = this.#addedLabel(item);
  }

  // Eyebrow copy for the essentials header. The popup also opens for non-racket
  // add-ons (they carry the `racket` tag), so derive the label from the item's
  // product_type — not #isAnchor (tag-based) — to avoid "Racket added" on a bag.
  #addedLabel(item) {
    const type = String(item?.product_type || '').trim();
    if (/rac[kq]u?et/i.test(type)) return this.#label('labelRacketAdded', 'Racket added');
    const label = type.replace(/^padel\s+/i, '').trim();
    return label ? this.#label('labelTypeAdded', '{type} added').replace('{type}', label) : this.#label('labelAddedToCart', 'Added to cart');
  }

  // Localized UI strings come from data-label-* attributes rendered by snippets/kit-upsell-popup.liquid
  // (locale keys padelusa.kit_upsell.*); English stays as the fallback.
  #label(key, fallback) {
    return this.dataset[key] || fallback;
  }

  #populateRecap(cart, item) {
    const image = this.querySelector('[data-kit-recap-image]');
    if (image && item.image) {
      image.src = item.image;
      image.alt = item.product_title || item.title || '';
    }
    const title = this.querySelector('[data-kit-recap-title]');
    if (title) title.textContent = item.product_title || item.title || '';

    const options = this.querySelector('[data-kit-recap-options]');
    if (options) {
      const parts = (item.options_with_values || [])
        .filter((option) => option.value && option.value !== 'Default Title')
        .map((option) => `${option.name}: ${option.value}`);
      options.textContent = parts.join(' · ') || (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '');
    }

    const count = this.querySelector('[data-kit-recap-count]');
    if (count) {
      // null/undefined = optimistic pending state (real numbers arrive with CartAddEvent).
      if (cart.item_count == null) {
        count.textContent = '';
      } else {
        const n = cart.item_count || 0;
        count.textContent = (n === 1 ? this.#label('labelItemOne', '{n} item') : this.#label('labelItemOther', '{n} items')).replace('{n}', String(n));
      }
    }
    const total = this.querySelector('[data-kit-recap-total]');
    if (total) total.textContent = cart.total_price == null ? '…' : this.#money(cart.total_price || 0);
  }

  /**
   * Tap = real cart write (Padel USA). Adding an essential POSTs it to the cart
   * immediately (no page reload) tagged `_added_from: kit-upsell-popup`, so the
   * purchase is attributable in admin / exports / Flow / Klaviyo; tapping again
   * removes it. The UI flips optimistically for snappiness and rolls back if the
   * request fails. #busy guards a double-tap from firing two writes for one row.
   */
  async #toggle(button) {
    const id = button.getAttribute('data-variant-id');
    if (this.#addonsPending || button.disabled || !id || this.#busy.has(id)) return;
    const item = button.closest('[data-kit-item]');
    const select = item?.querySelector('[data-kit-variant-select]');
    if (select) select.disabled = true;
    const adding = !this.#selected.has(id);

    // Optimistic UI — reflect the intent instantly, before the network round-trip.
    this.#busy.add(id);
    button.setAttribute('aria-pressed', adding ? 'true' : 'false');
    button.setAttribute('aria-busy', 'true');
    item?.classList.toggle('is-selected', adding);
    if (adding) this.#selected.add(id);
    else this.#selected.delete(id);
    this.#updateCta();

    const write = this.#commitAddon(id, adding);
    this.#inflight.push(write);
    const ok = await write;

    if (!ok) {
      // The cart never changed — roll the optimistic flip back.
      if (adding) this.#selected.delete(id);
      else this.#selected.add(id);
      button.setAttribute('aria-pressed', adding ? 'false' : 'true');
      item?.classList.toggle('is-selected', !adding);
      this.#updateCta();
    }
    if (select) select.disabled = this.#addonsPending;
    button.removeAttribute('aria-busy');
    this.#busy.delete(id);
    this.#inflight = this.#inflight.filter((p) => p !== write);
  }

  /**
   * Add or remove a single essential. Add carries the hidden `_added_from` line
   * property (underscore = stored on the order line, invisible in cart/checkout UI)
   * so popup-driven revenue is filterable later. Remove zeroes the variant's line;
   * safe here because #syncAddons hides items already in the cart, so the popup only
   * ever adds a brand-new line per variant. Returns true on success.
   * @param {string} id variant id
   * @param {boolean} adding
   * @returns {Promise<boolean>}
   */
  async #commitAddon(id, adding) {
    try {
      if (adding) {
        const response = await fetch(ROUTES.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(id), quantity: 1, properties: { _added_from: SOURCE } }],
          }),
        });
        if (response.ok) {
          this.#primeCartCache();
          // Legacy event (live Intelligems goal) — dataLayer + DOM only; the canonical one is published.
          this.#track('kit_upsell_addon_added', { variant: this.#currentVariant(), variant_id: String(id) }, false);
          const row = this.querySelector(`[data-kit-add][data-variant-id="${id}"]`)?.closest('[data-kit-item]');
          if (row) {
            this.#track('kit_upsell_added', {
              variant: this.#currentVariant(),
              items: [this.#rowInfo(row, this.#visibleRows().indexOf(row) + 1)],
            });
          }
        }
        return response.ok;
      }
      // Remove: resolve the popup-added line's KEY first — a variant id alone is ambiguous for
      // lines that carry properties, so zeroing by key can never touch an unrelated line.
      const cart = await this.#getCart();
      const line = (cart?.items || []).find(
        (l) => String(l.variant_id) === String(id) && l.properties && l.properties._added_from === SOURCE
      );
      const response = await fetch(ROUTES.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line?.key || String(id), quantity: 0 }),
      });
      if (response.ok) this.#primeCartCache();
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  #updateCta() {
    const cta = this.querySelector('[data-kit-cta]');
    if (!cta) return;
    const count = this.#selected.size;
    const labelDefault = cta.getAttribute('data-label-default') || 'Go to cart';
    const labelAdd = cta.getAttribute('data-label-add') || 'Add {count} & go to cart';
    cta.textContent = count > 0 ? labelAdd.replace('{count}', String(count)) : labelDefault;
  }

  #reset() {
    this.#selected.clear();
    this.querySelectorAll('[data-kit-variant-select]').forEach((select) => {
      select.value = '';
      this.#syncVariantSelect(select);
    });
    this.querySelectorAll('[data-kit-add]').forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.closest('[data-kit-item]')?.classList.remove('is-selected');
    });
    this.#updateCta();
  }

  /**
   * Navigate to `url`. Essentials are already committed to the cart on tap
   * (#toggle -> #commitAddon), so this only waits for in-flight writes — the
   * racket's optimistic add and any still-pending essential taps — so we never
   * leave the page mid-request, then routes to cart/checkout.
   */
  async #goWithSelected(url) {
    await this.#whenPendingSettled();
    if (this.#inflight.length) await Promise.allSettled(this.#inflight);
    // Straight to checkout skips /cart, where the Shipping Protection toggle re-tiers its line.
    // Correct the tier here so a low-tier line can't ride a bigger cart into checkout.
    if (url === '/checkout') await ensureShippingProtectionTier({ path: 'popup' });
    window.location.href = url;
  }

  #money(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Fire-and-forget A/B attach-rate analytics: pushes to window.dataLayer (GA4/GTM), dispatches a
   * bubbling document CustomEvent that Intelligems can register as a custom event and (unless
   * `publish` is false) Shopify.analytics.publish for the upsell custom pixel. Fully guarded —
   * instrumentation must NEVER throw into the popup (this runs inside a LIVE A/B experiment). Events:
   * `kit_upsell_shown` (once per open, in #applyVariant), `kit_upsell_added` and the legacy
   * `kit_upsell_addon_added` (per add), all labelled with the resolved `variant` per test arm.
   */
  #track(name, detail, publish = true) {
    try {
      const payload = { ...detail, placement: SOURCE };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...payload });
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
      if (publish) window.Shopify?.analytics?.publish?.(name, payload);
    } catch (_) {
      /* analytics must never break the popup */
    }
  }

  /** Resolved A/B variant for event labelling, read from the applied CSS class. */
  #currentVariant() {
    if (this.classList.contains('kit-upsell--recap-below')) return 'recap-below';
    if (this.classList.contains('kit-upsell--recap')) return 'recap';
    return 'essentials';
  }
}

if (!customElements.get('kit-upsell-dialog')) {
  customElements.define('kit-upsell-dialog', KitUpsellDialog);
}
